import React from "react";
import { useTranslation } from "react-i18next";
import { ReportFormData } from "../../types/ReportFormData";
import Input from "../ui/Input";
import DatePicker from "../ui/DatePicker";
import SearchableMultiSelect from "./SearchableMultiSelect";
import DaysCheckboxGroup from "./DaysCheckboxGroup";
import { getMedia, getScreens } from "../../services/reportService";
import { Media } from "../../types/Media";
import { Monitor } from "../../types/Screen";

interface GeneralParamsSectionProps {
	formData: ReportFormData;
	onChange: (data: Partial<ReportFormData>) => void;
	errors?: Partial<Record<keyof ReportFormData, string>>;
	screenOptions: Monitor[];
	loadingScreens: boolean;
	mediaOptions: Media[];
	loadingMedia: boolean;
}

const GeneralParamsSection: React.FC<GeneralParamsSectionProps> = ({
	formData,
	onChange,
	errors,
	screenOptions,
	loadingScreens,
	mediaOptions,
	loadingMedia,
}) => {
	const { t } = useTranslation();

	const handleFieldChange = (field: keyof ReportFormData, value: any) => {
		onChange({ [field]: value });
	};

	const getSelectedMedia = (): Media[] => {
		return formData.included_media_IDs
			.map((id) => mediaOptions.find((m) => m.id === id))
			.filter((m): m is Media => m !== undefined);
	};

	const getSelectedScreens = (): Monitor[] => {
		return formData.excluded_screen_IDs
			.map((id) => screenOptions.find((s) => s.Monitor_ID === id))
			.filter((s): s is Monitor => s !== undefined);
	};

	const mediaReady = !loadingMedia;
	const screensReady = !loadingScreens;

	return (
		<div className="space-y-6 pb-6 border-b border-gray-200">
			<h2 className="text-2xl font-bold text-gray-800 mb-6">
				{t("form.general.title")}
			</h2>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Input
					label={t("form.general.reportTitle")}
					value={formData.report_title}
					onChange={(e) => handleFieldChange("report_title", e.target.value)}
					error={errors?.report_title}
					required
					placeholder={t("form.general.reportTitlePlaceholder")}
				/>

				<Input
					label={t("form.general.clientName")}
					value={formData.client_name}
					onChange={(e) => handleFieldChange("client_name", e.target.value)}
					error={errors?.client_name}
					required
					placeholder={t("form.general.clientNamePlaceholder")}
				/>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Input
					label={t("form.general.campaignName")}
					value={formData.campaign_name}
					onChange={(e) => handleFieldChange("campaign_name", e.target.value)}
					error={errors?.campaign_name}
					required
					placeholder={t("form.general.campaignNamePlaceholder")}
				/>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<DatePicker
					label={t("form.general.startDate")}
					value={formData.start_date as any}
					onChange={(e) => {
						const dateValue = e.target.value ? new Date(e.target.value) : null;
						handleFieldChange("start_date", dateValue);
					}}
					error={errors?.start_date}
					required
				/>

				<DatePicker
					label={t("form.general.endDate")}
					value={formData.end_date as any}
					onChange={(e) => {
						const dateValue = e.target.value ? new Date(e.target.value) : null;
						handleFieldChange("end_date", dateValue);
					}}
					error={errors?.end_date}
					required
				/>
			</div>

			<div className="space-y-4">
				<SearchableMultiSelect<Media>
					label={t("form.general.includedMedia")}
					selectedItems={getSelectedMedia()}
					onChange={(items) =>
						handleFieldChange(
							"included_media_IDs",
							items.map((item) => item.id),
						)
					}
					fetchOptions={getMedia}
					preloadedOptions={mediaOptions}
					placeholder={t("form.general.includedMediaPlaceholder")}
					error={errors?.included_media_IDs}
					disabled={!mediaReady}
				/>

				<SearchableMultiSelect<Monitor>
					label={t("form.general.excludedScreens")}
					selectedItems={getSelectedScreens()}
					onChange={(items) =>
						handleFieldChange(
							"excluded_screen_IDs",
							items.map((item) => item.Monitor_ID),
						)
					}
					fetchOptions={getScreens}
					preloadedOptions={screenOptions}
					placeholder={t("form.general.excludedScreensPlaceholder")}
					error={errors?.excluded_screen_IDs}
					disabled={!screensReady}
				/>
			</div>

			<DaysCheckboxGroup
				label={t("form.general.days")}
				selectedDays={formData.days}
				onChange={(days) => handleFieldChange("days", days)}
				error={errors?.days}
			/>
		</div>
	);
};

export default GeneralParamsSection;
